import { ArrowDown, ArrowUp, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  PreviewScheduleFromTakeoffInput,
  ScheduleFromTakeoffPreview,
} from '@obraplan/core/application/contracts/schedule-from-takeoff';
import { isProjectVersion } from '@obraplan/core/domain/structure/project';
import { LoadingState } from '@/components/feedback/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { formatBusinessDate } from '@/lib/format';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';
import { TakeoffPreview } from './TakeoffPreview';
import {
  floorsInOrder,
  moveFloor,
  parseCrew,
  plural,
  resultadoDaGeracao,
} from './takeoff-preview-model';
import { motivoSemQuantitativo, type QuantitativoDoCronograma } from './use-takeoff-lines';

/** O fuso de quem ainda não leu a obra — o mesmo que a página usa para "hoje". */
const FUSO_PADRAO = 'America/Sao_Paulo';

/** A pausa depois da última mudança antes de pedir a proposta de novo. */
const ESPERA_PARA_RECALCULAR = 600;

const DATA = /^\d{4}-\d{2}-\d{2}$/;

interface EstadoDaPrevia {
  /** O pedido que gerou esta resposta — a tela compara com o atual para saber se envelheceu. */
  readonly pedido: PreviewScheduleFromTakeoffInput;
  readonly previa: ScheduleFromTakeoffPreview | undefined;
  readonly erro: string | undefined;
  readonly carregando: boolean;
}

export interface GenerateFromTakeoffDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** O quantitativo da obra, lido pela página (`useTakeoffLines`). */
  readonly takeoff: QuantitativoDoCronograma;
}

/**
 * "Gerar do quantitativo" (Fase 31, RN-415 a RN-418, D1, D4, D5).
 *
 * O sistema propõe e a pessoa ajusta: a data de início, a ordem dos pavimentos e a equipe de
 * cada serviço. A cada mudança a proposta é pedida de novo — depois de uma pausa, para não
 * pedir a cada tecla —, e "Recalcular" pede na hora. **Nada é gravado até confirmar.**
 *
 * A frente que falta (pavimento × disciplina) só é criada com a caixa marcada (RN-035): sem
 * ela, confirmar fica desligado e a tela diz por quê.
 */
export function GenerateFromTakeoffDialog({
  open,
  onOpenChange,
  takeoff,
}: GenerateFromTakeoffDialogProps) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();

  // "Hoje" no fuso da obra: é o início padrão.
  const obra = useLive(
    () =>
      open && session !== undefined && activeProjectId !== undefined
        ? container.getProject.execute(activeProjectId, session).then(
            (lida) => ({ lida }),
            () => ({ lida: undefined }),
          )
        : Promise.resolve(undefined),
    [open, session?.userId, activeProjectId],
  );
  // A obra que não pôde ser lida não trava a geração: vale o fuso padrão.
  const obraLida = obra !== undefined;
  const fuso = obra?.lida !== undefined && obra.lida.ok ? obra.lida.value.timezone : FUSO_PADRAO;
  // A versão de orçamento não gera cronograma: o servidor recusa (`PROJECT.IS_VERSION`), e a
  // tela diz antes de pedir.
  const ehVersao = obra?.lida !== undefined && obra.lida.ok && isProjectVersion(obra.lida.value);
  const hoje = useMemo(() => container.clock.today(fuso).toString(), [container, fuso]);

  const [inicioDigitado, setInicioDigitado] = useState<string | undefined>(undefined);
  const [ordem, setOrdem] = useState<readonly string[] | undefined>(undefined);
  const [equipesDigitadas, setEquipesDigitadas] = useState<Readonly<Record<string, string>>>({});
  const [criarFrentes, setCriarFrentes] = useState(false);
  const [enviado, setEnviado] = useState<PreviewScheduleFromTakeoffInput | undefined>(undefined);
  const [rodada, setRodada] = useState(0);
  const [estado, setEstado] = useState<EstadoDaPrevia | undefined>(undefined);
  const jaPediu = useRef(false);

  const inicio = inicioDigitado ?? hoje;
  const inicioValido = DATA.test(inicio);

  const equipes = useMemo(() => {
    const validas: { serviceId: string; people: number }[] = [];
    const invalidas = new Set<string>();
    for (const [serviceId, texto] of Object.entries(equipesDigitadas)) {
      const pessoas = parseCrew(texto);
      if (pessoas === undefined) invalidas.add(serviceId);
      else validas.push({ serviceId, people: pessoas });
    }
    return { validas, invalidas };
  }, [equipesDigitadas]);

  const linhas = takeoff.estado === 'PRONTO' ? takeoff.linhas : undefined;
  const pedido = useMemo<PreviewScheduleFromTakeoffInput | undefined>(() => {
    if (!open || activeProjectId === undefined || linhas === undefined || ehVersao)
      return undefined;
    // O "hoje" padrão espera o fuso da obra: pedir com o de outro fuso seria pedir duas vezes.
    if (inicioDigitado === undefined && !obraLida) return undefined;
    if (!inicioValido || equipes.invalidas.size > 0) return undefined;
    return {
      projectId: activeProjectId,
      startDate: inicio,
      ...(ordem === undefined ? {} : { floorOrder: [...ordem] }),
      ...(equipes.validas.length === 0 ? {} : { crews: equipes.validas }),
      takeoff: [...linhas],
    };
  }, [
    open,
    activeProjectId,
    linhas,
    ehVersao,
    inicioDigitado,
    obraLida,
    inicio,
    inicioValido,
    ordem,
    equipes,
  ]);

  // O pedido muda → a proposta é pedida depois da pausa. O primeiro sai na hora.
  useEffect(() => {
    if (pedido === undefined) return;
    const relogio = window.setTimeout(
      () => {
        jaPediu.current = true;
        setEnviado(pedido);
      },
      jaPediu.current ? ESPERA_PARA_RECALCULAR : 0,
    );
    return () => window.clearTimeout(relogio);
  }, [pedido]);

  useEffect(() => {
    if (enviado === undefined || session === undefined) return;
    let vivo = true;
    // A proposta anterior fica na tela enquanto a nova não chega: a lista não pisca a cada
    // número digitado na equipe.
    setEstado((atual) => ({
      pedido: enviado,
      previa: atual?.previa,
      erro: undefined,
      carregando: true,
    }));
    container.previewScheduleFromTakeoff.execute(enviado, session).then(
      (resposta) => {
        if (!vivo) return;
        // Na recusa, a proposta anterior fica: a ordem e a equipe continuam na tela para a
        // pessoa desfazer o que o servidor recusou. Confirmar fica desligado até a próxima.
        setEstado((atual) =>
          resposta.ok
            ? { pedido: enviado, previa: resposta.value, erro: undefined, carregando: false }
            : {
                pedido: enviado,
                previa: atual?.previa,
                erro: resposta.error.message,
                carregando: false,
              },
        );
      },
      () => {
        if (!vivo) return;
        setEstado((atual) => ({
          pedido: enviado,
          previa: atual?.previa,
          erro: 'Não foi possível calcular a proposta — verifique a conexão.',
          carregando: false,
        }));
      },
    );
    return () => {
      vivo = false;
    };
  }, [enviado, rodada, session, container]);

  const previa = estado?.previa;
  const atualizada =
    pedido !== undefined &&
    estado !== undefined &&
    estado.pedido === pedido &&
    !estado.carregando &&
    estado.erro === undefined;
  const faltam = previa?.missingFronts.length ?? 0;

  const [confirmar, confirmacao] = useCommand(
    (confirmado: PreviewScheduleFromTakeoffInput) =>
      container.confirmScheduleFromTakeoff.execute(
        { ...confirmado, createMissingFronts: criarFrentes },
        session!,
      ),
    {
      silent: true,
      successMessage: resultadoDaGeracao,
      onSuccess: () => fechar(),
    },
  );

  const limpar = (): void => {
    setInicioDigitado(undefined);
    setOrdem(undefined);
    setEquipesDigitadas({});
    setCriarFrentes(false);
    setEnviado(undefined);
    setEstado(undefined);
    jaPediu.current = false;
  };
  const fechar = (): void => {
    limpar();
    onOpenChange(false);
  };
  const aoMudarAberto = (proximo: boolean): void => {
    if (!proximo) limpar();
    onOpenChange(proximo);
  };

  const recalcular = (): void => {
    if (pedido === undefined) return;
    jaPediu.current = true;
    setEnviado(pedido);
    setRodada((atual) => atual + 1);
  };

  const pavimentos =
    previa === undefined ? [] : floorsInOrder(previa.floors, ordem ?? previa.floorOrder);
  const ordemAtual = pavimentos.map((pavimento) => pavimento.floorId);

  const motivoDoBloqueio =
    previa === undefined
      ? undefined
      : previa.items.length === 0
        ? 'Nada a gerar: nenhum pavimento × serviço tem número.'
        : faltam > 0 && !criarFrentes
          ? `Marque “${rotuloDasFrentes(faltam)}” para confirmar: sem a frente, o item não tem onde ficar.`
          : undefined;
  const podeConfirmar =
    previa !== undefined && atualizada && motivoDoBloqueio === undefined && !confirmacao.isPending;

  const semQuantitativo = motivoSemQuantitativo(takeoff);
  // A recusa antes da primeira proposta vai para o topo: as listas não têm o que mostrar, e
  // "Calculando…" nelas prometeria uma resposta que não vem (16/10/2026).
  const recusaSemProposta = previa === undefined ? estado?.erro : undefined;
  const enquantoNaoHaProposta = recusaSemProposta === undefined ? 'Calculando…' : 'Sem proposta.';

  return (
    <Dialog open={open} onOpenChange={aoMudarAberto}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Gerar do quantitativo</DialogTitle>
          <DialogDescription>
            Um item por pavimento × serviço com número no quantitativo. A duração sai do HH ÷
            (equipe × jornada), e cada serviço sobe pavimento a pavimento. Nada é gravado até você
            confirmar.
          </DialogDescription>
        </DialogHeader>

        {takeoff.estado === 'CARREGANDO' || takeoff.estado === 'DESLIGADO' ? (
          <LoadingState label="Lendo o quantitativo da obra…" />
        ) : semQuantitativo !== undefined ? (
          <p role="alert" className="text-sm text-(--color-state-warn)">
            {semQuantitativo}
          </p>
        ) : ehVersao ? (
          <p role="alert" className="text-sm text-(--color-state-warn)">
            Esta obra é uma versão de orçamento: a execução acompanha a versão aprovada. Gere o
            cronograma na obra aprovada.
          </p>
        ) : (
          <div className="flex min-w-0 flex-col gap-5">
            {recusaSemProposta !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {recusaSemProposta}
              </p>
            )}
            <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-5">
                <Field
                  label="Data de início"
                  htmlFor="gerar-inicio"
                  required
                  error={inicioValido ? undefined : 'Escolha a data de início.'}
                  hint={
                    previa !== undefined && previa.startDate !== inicio
                      ? `O primeiro dia útil a partir dela é ${formatBusinessDate(previa.startDate)}.`
                      : 'O primeiro item começa no primeiro dia útil a partir dela (calendário da obra).'
                  }
                >
                  <Input
                    id="gerar-inicio"
                    type="date"
                    value={inicio}
                    onChange={(event) => setInicioDigitado(event.target.value)}
                  />
                </Field>

                <section aria-label="Ordem dos pavimentos" className="flex min-w-0 flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-medium">Ordem dos pavimentos</h3>
                    {ordem !== undefined && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => setOrdem(undefined)}
                      >
                        Voltar à ordem padrão
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-(--color-content-muted)">
                    Cada serviço sobe nesta ordem. O padrão vai do nível mais baixo ao mais alto.
                  </p>
                  {pavimentos.length === 0 ? (
                    <p className="text-sm text-(--color-content-muted)">
                      {previa === undefined ? enquantoNaoHaProposta : 'A obra não tem pavimento.'}
                    </p>
                  ) : (
                    <ol className="flex max-h-64 min-w-0 flex-col overflow-y-auto rounded-md border border-(--color-border-subtle)">
                      {pavimentos.map((pavimento, indice) => {
                        const nome = `${pavimento.towerName} · ${pavimento.floorName}`;
                        return (
                          <li
                            key={pavimento.floorId}
                            className="flex min-w-0 items-center gap-2 border-b border-(--color-border-subtle) px-2 py-1 last:border-0"
                          >
                            <span className="w-6 shrink-0 text-right text-xs text-(--color-content-muted) tabular">
                              {indice + 1}.
                            </span>
                            {/* No celular o selo desce para baixo do nome, em vez de cortá-lo. */}
                            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                              <span className="min-w-0 truncate text-sm" title={nome}>
                                {nome}
                              </span>
                              {!pavimento.hasTakeoff && (
                                <Badge
                                  tone="neutral"
                                  className="whitespace-nowrap"
                                  title="Nenhuma tipologia com número está no desenho deste pavimento"
                                >
                                  sem quantitativo
                                </Badge>
                              )}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Subir ${nome}`}
                              disabled={indice === 0}
                              onClick={() => setOrdem(moveFloor(ordemAtual, pavimento.floorId, -1))}
                            >
                              <ArrowUp aria-hidden="true" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Descer ${nome}`}
                              disabled={indice === pavimentos.length - 1}
                              onClick={() => setOrdem(moveFloor(ordemAtual, pavimento.floorId, 1))}
                            >
                              <ArrowDown aria-hidden="true" />
                            </Button>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </section>
              </div>

              <section aria-label="Equipe por serviço" className="flex min-w-0 flex-col gap-2">
                <h3 className="text-sm font-medium">Equipe por serviço</h3>
                <p className="text-xs text-(--color-content-muted)">
                  Pessoas que executam o serviço. O padrão são os funcionários ativos com função
                  vinculada à disciplina (Mão de obra), no mínimo um.
                </p>
                {previa === undefined || previa.crews.length === 0 ? (
                  <p className="text-sm text-(--color-content-muted)">
                    {previa === undefined ? enquantoNaoHaProposta : 'Nenhum serviço na proposta.'}
                  </p>
                ) : (
                  <ul className="flex max-h-[22rem] min-w-0 flex-col overflow-y-auto rounded-md border border-(--color-border-subtle)">
                    {previa.crews.map((equipe) => {
                      const id = `equipe-${equipe.serviceId}`;
                      const texto = equipesDigitadas[equipe.serviceId] ?? String(equipe.people);
                      const invalida = equipes.invalidas.has(equipe.serviceId);
                      return (
                        <li
                          key={equipe.serviceId}
                          className="grid min-w-0 grid-cols-[minmax(0,1fr)_5rem] items-center gap-x-3 gap-y-0.5 border-b border-(--color-border-subtle) px-3 py-2 last:border-0"
                        >
                          <label htmlFor={id} className="min-w-0 text-sm">
                            <span className="text-(--color-content-muted)">
                              {equipe.disciplineName} ·{' '}
                            </span>
                            {equipe.serviceName}
                          </label>
                          <Input
                            id={id}
                            inputMode="numeric"
                            className="text-right tabular"
                            aria-label={`Equipe de ${equipe.serviceName}`}
                            aria-invalid={invalida}
                            aria-describedby={`${id}-dica`}
                            value={texto}
                            onChange={(event) =>
                              setEquipesDigitadas((atuais) => ({
                                ...atuais,
                                [equipe.serviceId]: event.target.value,
                              }))
                            }
                          />
                          <p
                            id={`${id}-dica`}
                            className={
                              invalida
                                ? 'col-span-2 text-xs text-(--color-state-danger)'
                                : 'col-span-2 text-xs text-(--color-content-muted)'
                            }
                          >
                            {invalida
                              ? 'Um número inteiro de 1 a 1.000 pessoas.'
                              : `Padrão ${String(equipe.defaultPeople)} · ${textoDosVinculados(equipe.linkedEmployees)}`}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>

            <section aria-label="Proposta" className="flex min-w-0 flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-medium">Proposta</h3>
                {previa !== undefined &&
                (estado?.carregando === true ||
                  (pedido !== undefined && estado?.pedido !== pedido)) ? (
                  <span role="status" className="text-xs text-(--color-content-muted)">
                    Recalculando…
                  </span>
                ) : null}
              </div>

              {/* Com proposta na tela, a recusa fica aqui, junto dela; sem, ela já está no topo. */}
              {estado?.erro !== undefined && previa !== undefined && (
                <p role="alert" className="text-sm text-(--color-state-danger)">
                  {estado.erro}
                </p>
              )}

              {previa === undefined ? (
                recusaSemProposta === undefined && <LoadingState label="Calculando a proposta…" />
              ) : (
                <>
                  {faltam > 0 && (
                    <div className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3">
                      <div className="flex items-start gap-2">
                        <input
                          id="gerar-criar-frentes"
                          type="checkbox"
                          className="mt-1"
                          checked={criarFrentes}
                          onChange={(event) => setCriarFrentes(event.target.checked)}
                        />
                        <div className="flex min-w-0 flex-col">
                          <label htmlFor="gerar-criar-frentes" className="text-sm font-medium">
                            {rotuloDasFrentes(faltam)}
                          </label>
                          <p className="text-xs text-(--color-content-muted)">
                            A frente é o pavimento × a disciplina do serviço. Sem ela, o item não
                            tem onde ficar.
                          </p>
                        </div>
                      </div>
                      <ul
                        className="flex flex-wrap gap-1.5 text-xs"
                        aria-label="Frentes que faltam"
                      >
                        {previa.missingFronts.slice(0, 12).map((frente) => (
                          <li
                            key={`${frente.floorId}|${frente.disciplineId}`}
                            className="rounded-full border border-(--color-border-subtle) px-2 py-0.5"
                          >
                            {frente.towerName} · {frente.floorName} — {frente.disciplineName}
                          </li>
                        ))}
                        {faltam > 12 && (
                          <li className="px-2 py-0.5 text-(--color-content-muted)">
                            e mais {faltam - 12}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                  <TakeoffPreview preview={previa} />
                </>
              )}
            </section>

            <p className="rounded-md bg-(--color-surface-muted) p-3 text-xs text-(--color-content-muted)">
              O item novo nasce com a linha de base igual à proposta (RN-322). O item que já existe
              mantém as datas e o avanço: só a quantidade e o HH passam a ser do quantitativo, e o
              número do arquivo fica guardado para comparar.
            </p>

            {confirmacao.error !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {confirmacao.error.message}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {motivoDoBloqueio !== undefined && (
            // `order-last`: no celular o rodapé é uma coluna invertida, e o aviso fica em cima
            // dos botões; no monitor, à esquerda deles.
            <p className="order-last text-xs text-(--color-state-warn) sm:order-first sm:mr-auto sm:max-w-md sm:self-center">
              {motivoDoBloqueio}
            </p>
          )}
          <Button type="button" variant="secondary" onClick={() => aoMudarAberto(false)}>
            Cancelar
          </Button>
          {linhas !== undefined && (
            <Button
              type="button"
              variant="secondary"
              disabled={pedido === undefined || estado?.carregando === true}
              onClick={recalcular}
            >
              <RefreshCw aria-hidden="true" />
              Recalcular
            </Button>
          )}
          {linhas !== undefined && (
            <Button
              type="button"
              variant="primary"
              disabled={!podeConfirmar}
              onClick={() => {
                if (pedido !== undefined) void confirmar(pedido);
              }}
            >
              {confirmacao.isPending ? 'Gerando…' : 'Gerar cronograma'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function rotuloDasFrentes(faltam: number): string {
  return faltam === 1
    ? 'Criar a frente que falta'
    : `Criar as ${String(faltam)} frentes que faltam`;
}

function textoDosVinculados(vinculados: number): string {
  if (vinculados === 0) return 'nenhum funcionário vinculado';
  return plural(vinculados, 'funcionário vinculado', 'funcionários vinculados');
}
